import React from 'react';
import { Spinner } from './Spinner';

interface ChunkErrorBoundaryProps {
  children: React.ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  reloading: boolean;
}

/**
 * Catches "Failed to fetch dynamically imported module" errors that occur
 * after a frontend deploy: the browser holds an old index.html referencing
 * old chunk hashes that no longer exist on the server.
 *
 * On such errors we hard-reload once (guarded via sessionStorage) so the
 * browser fetches the fresh index.html with the new chunk names. If the
 * reload fails again, we show a friendly manual-reload UI instead of the
 * React Router error screen.
 */
export class ChunkErrorBoundary extends React.Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { hasError: false, reloading: false };

  static getDerivedStateFromError(): Partial<ChunkErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('ChunkLoadError');

    if (!isChunkError) {
      // Re-throw non-chunk errors so the regular error screen handles them
      throw error;
    }

    // Guard against reload loops (e.g. the chunk is genuinely broken)
    const KEY = 'agentic_seo_chunk_reload';
    if (!sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, '1');
      this.setState({ reloading: true });
      window.location.reload();
    } else {
      sessionStorage.removeItem(KEY);
      this.setState({ reloading: false });
    }
  }

  handleReload = () => {
    sessionStorage.removeItem('agentic_seo_chunk_reload');
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4 p-8 text-center">
        {this.state.reloading ? (
          <>
            <Spinner />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              New version detected — reloading…
            </p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-500/10 flex items-center justify-center text-2xl">
              🔄
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Update available
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
              A new version of the app was deployed while this page was open.
              Please reload to continue.
            </p>
            <button
              onClick={this.handleReload}
              className="mt-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Reload now
            </button>
          </>
        )}
      </div>
    );
  }
}
